import React from 'react';
import { formatDate } from '../../utils/stringUtils';
import { getPayslipViewData, formatInr } from '../../utils/payslipViewData';

const formatPeriodString = (periodString) => {
  if (!periodString) return 'N/A';
  const parts = periodString.split(' - ');
  if (parts.length !== 2) return periodString;
  try {
    const parseDate = (dateStr) => {
      const [day, month, year] = dateStr.trim().split('/');
      return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
    };
    const startDate = parseDate(parts[0]);
    const endDate = parseDate(parts[1]);
    if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
      return periodString;
    }
    return `${formatDate(startDate, { includeTime: false })} - ${formatDate(endDate, { includeTime: false })}`;
  } catch {
    return periodString;
  }
};

const structureTypeLabel = (type) => {
  if (type === 'earning') return 'Earning';
  if (type === 'reimbursement') return 'Reimbursement';
  if (type === 'deduction') return 'Deduction';
  return type || 'Component';
};

const PayslipDetailView = ({ payslip }) => {
  if (!payslip) return null;

  const {
    earnings,
    reimbursements,
    deductions,
    grossPay,
    proRatedGrossPay,
    totalDeductions,
    attendanceDeduction,
    presentDays,
    workingDays,
    showProRataNote,
    showAttendanceDeductionRow,
    salaryStructure,
    netPay
  } = getPayslipViewData(payslip);

  const structureComponents = salaryStructure?.components || [];

  return (
    <div className="p-8">
      <div className="bg-gradient-to-r from-agrosaf-green to-agrosaf-blue p-6 text-white mb-6">
        <div className="text-center mb-4">
          <h2 className="text-2xl font-bold mb-2">{process.env.REACT_APP_COMPANY_NAME}</h2>
          <p className="text-sm opacity-90">
            PAYSLIP FOR {formatPeriodString(payslip?.period).toUpperCase()}
          </p>
        </div>
      </div>

      <table className="w-full border-collapse mb-6" style={{ tableLayout: 'fixed' }}>
        <colgroup>
          <col style={{ width: '20%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '20%' }} />
          <col style={{ width: '30%' }} />
        </colgroup>
        <thead>
          <tr className="bg-gray-50">
            <th colSpan="4" className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
              EMPLOYEE INFORMATION
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Employee Name</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
              {payslip.employeeName || 'N/A'}
            </td>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Employee ID</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
              {payslip.employeeId || 'N/A'}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Email</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
              {payslip.email || 'N/A'}
            </td>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Department</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
              {payslip.department || 'N/A'}
            </td>
          </tr>
          <tr>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Pay Period</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
              {formatPeriodString(payslip?.period)}
            </td>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Status</td>
            <td className="border border-gray-300 px-4 py-2 text-gray-900 capitalize">
              {payslip?.status || 'N/A'}
            </td>
          </tr>
          {presentDays !== undefined && workingDays !== undefined && (
            <>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Working Days</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-900">
                  {presentDays} / {workingDays}
                </td>
                <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Approved Leaves</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-900">
                  {payslip.approvedLeavesCount ?? 0}
                </td>
              </tr>
              <tr>
                <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Payslip ID</td>
                <td colSpan="3" className="border border-gray-300 px-4 py-2 text-gray-900 break-words">
                  {payslip.payslipId || payslip.id || 'N/A'}
                </td>
              </tr>
            </>
          )}
        </tbody>
      </table>

      {structureComponents.length > 0 && (
        <table className="w-full border-collapse mb-6">
          <thead>
            <tr className="bg-gray-100">
              <th colSpan="3" className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
                SALARY STRUCTURE (AT GENERATION)
              </th>
            </tr>
            <tr className="bg-gray-50 text-xs text-gray-600">
              <th className="border border-gray-300 px-4 py-2 text-left">Component</th>
              <th className="border border-gray-300 px-4 py-2 text-left">Type</th>
              <th className="border border-gray-300 px-4 py-2 text-right">Monthly Amount</th>
            </tr>
          </thead>
          <tbody>
            {structureComponents.map((comp, index) => (
              <tr key={`struct-${index}`} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-4 py-2 text-gray-700">{comp.name || 'N/A'}</td>
                <td className="border border-gray-300 px-4 py-2 text-gray-600 capitalize">
                  {structureTypeLabel(comp.type)}
                </td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium text-gray-900">
                  {formatInr(Number(comp.value || 0))}
                </td>
              </tr>
            ))}
            {salaryStructure?.netPay != null && (
              <tr className="bg-gray-50 font-semibold">
                <td colSpan="2" className="border border-gray-300 px-4 py-2 text-gray-900">
                  Structure Net Pay (Monthly)
                </td>
                <td className="border border-gray-300 px-4 py-2 text-right text-gray-900">
                  {formatInr(salaryStructure.netPay)}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}

      <table className="w-full border-collapse mb-6">
        <thead>
          <tr className="bg-green-50">
            <th colSpan="2" className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
              EARNINGS & REIMBURSEMENTS (PAYABLE)
            </th>
          </tr>
          {showProRataNote && (
            <tr>
              <td colSpan="2" className="border border-gray-300 px-4 py-1 text-xs text-gray-600 bg-green-50">
                Pro-rated based on {presentDays} payable days out of {workingDays} calendar days
              </td>
            </tr>
          )}
        </thead>
        <tbody>
          {earnings.length > 0 ? (
            earnings.map((earning, index) => (
              <tr key={`earning-${index}`} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-4 py-2 text-gray-700">{earning.name || 'N/A'}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium text-green-600">
                  {formatInr(earning.amount)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="border border-gray-300 px-4 py-2 text-gray-500 text-center">
                No earnings listed
              </td>
            </tr>
          )}
          {reimbursements.map((reimbursement, index) => (
            <tr key={`reimbursement-${index}`} className="hover:bg-gray-50">
              <td className="border border-gray-300 px-4 py-2 text-gray-700">{reimbursement.name || 'N/A'}</td>
              <td className="border border-gray-300 px-4 py-2 text-right font-medium text-blue-600">
                {formatInr(reimbursement.amount)}
              </td>
            </tr>
          ))}
          <tr className="bg-green-50 font-semibold">
            <td className="border border-gray-300 px-4 py-2 text-gray-900">Total Earnings + Reimbursements</td>
            <td className="border border-gray-300 px-4 py-2 text-right text-green-700">{formatInr(netPay)}</td>
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse mb-6">
        <thead>
          <tr className="bg-red-50">
            <th colSpan="2" className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
              DEDUCTIONS
            </th>
          </tr>
        </thead>
        <tbody>
          {deductions.length > 0 ? (
            deductions.map((deduction, index) => (
              <tr key={`deduction-${index}`} className="hover:bg-gray-50">
                <td className="border border-gray-300 px-4 py-2 text-gray-700">{deduction.name || 'N/A'}</td>
                <td className="border border-gray-300 px-4 py-2 text-right font-medium text-red-600">
                  {formatInr(deduction.amount)}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="border border-gray-300 px-4 py-2 text-gray-500 text-center">
                No structure deductions listed
              </td>
            </tr>
          )}
          {showAttendanceDeductionRow && (
            <tr className="hover:bg-gray-50">
              <td className="border border-gray-300 px-4 py-2 text-gray-700">
                Attendance / unpaid leave adjustment
              </td>
              <td className="border border-gray-300 px-4 py-2 text-right font-medium text-red-600">
                {formatInr(attendanceDeduction)}
              </td>
            </tr>
          )}
          <tr className="bg-red-50 font-semibold">
            <td className="border border-gray-300 px-4 py-2 text-gray-900">Total Deductions</td>
            <td className="border border-gray-300 px-4 py-2 text-right text-red-700">{formatInr(totalDeductions)}</td>
          </tr>
        </tbody>
      </table>

      <table className="w-full border-collapse mb-6">
        <thead>
          <tr className="bg-blue-50">
            <th colSpan="2" className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-900">
              PAY SUMMARY
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">
              Gross Pay (Earnings + Reimbursements)
            </td>
            <td className="border border-gray-300 px-4 py-2 text-right font-semibold text-gray-900">
              {formatInr(grossPay)}
            </td>
          </tr>
          {payslip.incentive > 0 && (
            <tr>
              <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">
                Incentive
              </td>
              <td className="border border-gray-300 px-4 py-2 text-right font-semibold text-green-600">
                {formatInr(payslip.incentive)}
              </td>
            </tr>
          )}
          <tr>
            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700">Total Deductions</td>
            <td className="border border-gray-300 px-4 py-2 text-right font-semibold text-red-600">
              {formatInr(totalDeductions)}
            </td>
          </tr>
          <tr className="bg-blue-100 border-2 border-blue-500">
            <td className="border border-blue-500 px-4 py-3 font-bold text-gray-900 text-lg">
              NET PAY (Amount Payable)
            </td>
            <td className="border border-blue-500 px-4 py-3 text-right font-bold text-blue-700 text-xl">
              {formatInr(netPay)}
            </td>
          </tr>
        </tbody>
      </table>

      <div className="text-center text-xs text-gray-500 mt-6 pt-4 border-t border-gray-200">
        <p>This is a computer-generated payslip and does not require a signature.</p>
        {payslip?.generatedDate && (
          <p className="mt-1 text-gray-400 italic">
            Generated on: {formatDate(payslip.generatedDate, { includeTime: true })}
          </p>
        )}
      </div>
    </div>
  );
};

export default PayslipDetailView;
